<template>
  <div class="checkin-panel">
    <vx-card class="mb-base">
      <div class="cp-header">
        <div>
          <h4 class="mb-0">{{ itemName || 'Check-in do evento' }}</h4>
          <small class="cp-muted">{{ typeLabel }}</small>
        </div>
        <div class="cp-header__actions">
          <label class="cp-muted mr-2" for="cp-date">Dia</label>
          <input id="cp-date" v-model="date" type="date" class="cp-date" @change="reload" />
          <vs-button size="small" type="border" class="ml-2" @click="reload">Atualizar</vs-button>
        </div>
      </div>
    </vx-card>

    <!-- KPIs ---------------------------------------------------------- -->
    <div class="vx-row mb-base">
      <div v-for="metric in metrics" :key="metric.label" class="vx-col w-1/2 md:w-1/5 mb-4 md:mb-0">
        <vx-card class="cp-metric">
          <div class="cp-metric__value">{{ metric.value }}</div>
          <div class="cp-metric__label">{{ metric.label }}</div>
        </vx-card>
      </div>
    </div>

    <div class="vx-row">
      <!-- Entradas ---------------------------------------------------- -->
      <div class="vx-col w-full lg:w-2/3 mb-base">
        <vx-card title="Entradas do dia">
          <p v-if="loading" class="cp-muted mb-0">Carregando...</p>
          <p v-else-if="!entries.length" class="cp-muted mb-0">
            Nenhuma entrada registrada neste dia.
          </p>
          <table v-else class="cp-table">
            <thead>
              <tr>
                <th>Pessoa</th>
                <th>Hora</th>
                <th>Canal</th>
                <th>Validado por</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in entries" :key="entry.id" :class="{ 'cp-row--undone': entry.undone_at }">
                <td>
                  {{ entry.holder }}
                  <div v-if="entry.ticket_code" class="cp-muted cp-code">{{ entry.ticket_code }}</div>
                </td>
                <td class="cp-num">{{ (entry.at || '').slice(11, 16) }}</td>
                <td>{{ entry.channel_label }}</td>
                <td>{{ entry.validated_by }}</td>
                <td class="cp-actions">
                  <button
                    v-if="!entry.undone_at"
                    type="button"
                    class="cp-link"
                    @click="undo(entry)"
                  >
                    Desfazer
                  </button>
                  <span v-else class="cp-muted">desfeita</span>
                </td>
              </tr>
            </tbody>
          </table>
        </vx-card>
      </div>

      <!-- Validadores ------------------------------------------------- -->
      <div class="vx-col w-full lg:w-1/3 mb-base">
        <vx-card title="Validadores">
          <p class="cp-muted">
            Quem pode validar ingresso deste evento na portaria. Quem responde pelo
            estabelecimento já valida sem estar nesta lista.
          </p>

          <div v-if="canManage" class="mb-3">
            <label class="cp-label" for="cp-validator-term">Nome ou e-mail</label>
            <input
              id="cp-validator-term"
              v-model="term"
              type="text"
              class="cp-input"
              autocomplete="off"
              placeholder="ana@clube.com.br"
              @input="onTermInput"
              @keyup.enter="addValidator" />

            <p v-if="searching" class="cp-muted mt-1 mb-0">Procurando...</p>

            <!-- Achou: confirma-se a pessoa antes de designar. -->
            <ul v-else-if="candidates.length" class="cp-candidates">
              <li v-for="candidate in candidates" :key="candidate.id">
                <button
                  type="button"
                  :class="['cp-candidate', { 'cp-candidate--on': picked && picked.id === candidate.id }]"
                  @click="pick(candidate)">
                  <span class="cp-list__name">{{ candidate.name }}</span>
                  <span class="cp-muted cp-list__meta">{{ candidate.email }}</span>
                </button>
              </li>
            </ul>

            <!--
              Não achou. O e-mail vira convite; um nome solto, não — sem e-mail não
              há para onde mandar nem como reconhecer a pessoa quando ela se
              cadastrar.
            -->
            <p v-else-if="searched && isEmail" class="cp-invite mt-1 mb-0">
              Ninguém com este e-mail ainda. Dá para convidar: a pessoa recebe o
              link, cria a conta dela e o acesso é liberado sozinho.
            </p>
            <p v-else-if="searched" class="cp-muted mt-1 mb-0">
              Nenhuma pessoa encontrada. Digite o <strong>e-mail completo</strong>
              para convidar alguém de fora.
            </p>

            <div v-if="searched && isEmail && !candidates.length" class="mt-2">
              <label class="cp-label" for="cp-validator-name">Nome (opcional)</label>
              <input
                id="cp-validator-name"
                v-model="inviteName"
                type="text"
                class="cp-input"
                autocomplete="off"
                placeholder="Como chamar esta pessoa no convite" />
            </div>

            <label v-if="canSubmit" class="cp-checkbox mt-2">
              <input v-model="applyToAll" type="checkbox" />
              <span>Validar em todos os meus eventos com ingresso</span>
            </label>

            <vs-button
              class="mt-2 w-full"
              size="small"
              :disabled="!canSubmit || adding"
              @click="addValidator">
              {{ submitLabel }}
            </vs-button>
          </div>

          <ul class="cp-list">
            <li v-for="validator in validators" :key="validator.id" class="cp-list__item">
              <div class="cp-list__who">
                <div class="cp-list__name">
                  {{ validator.name || validator.email }}
                  <span v-if="validator.pending" class="cp-badge">convite enviado</span>
                </div>
                <div class="cp-muted cp-list__meta">{{ validator.email }}</div>
                <div v-if="validator.pending" class="cp-muted cp-list__meta">
                  Vale assim que a pessoa criar a conta com este e-mail.
                </div>
              </div>
              <button v-if="canManage" type="button" class="cp-link cp-link--danger" @click="removeValidator(validator)">
                Remover
              </button>
            </li>
            <li v-if="!validators.length" class="cp-muted">Nenhum validador designado.</li>
          </ul>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import TicketService from '@/services/api/TicketService'

/**
 * Painel de check-in de um evento (aula ou espaço de locação).
 *
 * "Esperados" vem da AGENDA, não da contagem de ingressos: numa aula recorrente
 * uma credencial cobre a temporada inteira, então o número de ingressos emitidos
 * não diz nada sobre quantas pessoas têm sessão hoje.
 *
 * A designação de validador tem campo próprio, e não o `SelectSuggestion`
 * genérico, por duas razões. A primeira é privacidade: aquele componente consulta
 * `/api/v1/suggestion/user`, que responde com a tabela `users` inteira a qualquer
 * autenticado — um clube digitando "ana" recebia as Anas da plataforma inteira,
 * com e-mail e último acesso. A segunda é que ele só sabe escolher entre contas
 * que já existem, e a pessoa da portaria quase nunca é usuária: sem o caminho do
 * convite, o recurso não tinha como ser usado.
 */
export default {
  name: 'TicketCheckinPanel',
  data() {
    return {
      service: TicketService.build(this.$vs),
      // O Mural manda a data da ocorrência que a pessoa estava olhando
      // (?date=2026-08-20); sem o parâmetro, cai em hoje.
      date: this.$route.query.date || new Date().toISOString().slice(0, 10),
      summary: null,
      entries: [],
      validators: [],
      term: '',
      candidates: [],
      picked: null,
      inviteName: '',
      applyToAll: false,
      searching: false,
      // Só depois de uma busca concluída a tela pode dizer "ninguém encontrado" —
      // antes disso ela não sabe.
      searched: false,
      searchTimer: null,
      loading: true,
      adding: false,
      itemName: ''
    }
  },
  computed: {
    referenceType() {
      return this.$route.params.referenceType
    },
    referenceId() {
      return Number(this.$route.params.referenceId)
    },
    typeLabel() {
      return this.referenceType === 'rental_space' ? 'Espaço de locação' : 'Aula'
    },
    canManage() {
      return this.$acl && this.$acl.hasPermission('tickets.manage')
    },
    isEmail() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.term.trim())
    },
    /** Ou uma pessoa escolhida da lista, ou um e-mail para convidar. */
    canSubmit() {
      return Boolean(this.picked) || this.isEmail
    },
    submitLabel() {
      if (this.adding) return 'Salvando...'

      return this.picked ? 'Adicionar validador' : 'Enviar convite'
    },
    metrics() {
      const s = this.summary || {}

      return [
        { label: 'Ingressos ativos', value: s.issued != null ? s.issued : '—' },
        { label: 'Esperados hoje', value: s.expected != null ? s.expected : '—' },
        { label: 'Entraram', value: s.checked_in != null ? s.checked_in : '—' },
        {
          label: 'Presença',
          // Nulo e não 0%: num dia sem sessão, "0% de presença" é leitura errada.
          value: s.attendance_rate != null ? `${s.attendance_rate}%` : '—'
        },
        { label: 'Recusas', value: s.refused != null ? s.refused : '—' }
      ]
    }
  },
  created() {
    this.reload()
    this.loadValidators()
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    async reload() {
      this.loading = true

      try {
        const [summary, list] = await Promise.all([
          this.service.summary(this.referenceType, this.referenceId, this.date),
          this.service.list(this.referenceType, this.referenceId, this.date)
        ])

        this.summary = summary
        this.itemName = (summary && summary.item_name) || ''
        this.entries = (list && list.model && list.model.data) || []
      } catch (error) {
        this.entries = []
      } finally {
        this.loading = false
      }
    },

    async loadValidators() {
      if (!this.canManage) return

      try {
        const data = await this.service.getValidators(this.referenceType, this.referenceId)
        this.validators = Array.isArray(data) ? data : []
      } catch (error) {
        this.validators = []
      }
    },

    /**
     * Busca com atraso: o campo aceita e-mail, e disparar a cada tecla mandaria
     * meia dúzia de prefixos ao servidor antes do endereço completo.
     */
    onTermInput() {
      this.picked = null
      this.searched = false
      this.candidates = []

      if (this.searchTimer) clearTimeout(this.searchTimer)

      const term = this.term.trim()

      if (term.length < 3) {
        this.searching = false

        return
      }

      this.searching = true
      this.searchTimer = setTimeout(() => this.searchCandidates(term), 450)
    },

    async searchCandidates(term) {
      try {
        const data = await this.service.searchValidatorCandidates(term)
        // Descarta resposta atrasada de um termo que o operador já trocou.
        if (term !== this.term.trim()) return

        this.candidates = Array.isArray(data) ? data : []
        // Um único resultado é o caso normal da busca por e-mail exato: já vem
        // escolhido, para o operador não ter de tocar duas vezes no mesmo nome.
        if (this.candidates.length === 1) this.picked = this.candidates[0]
      } catch (error) {
        this.candidates = []
      } finally {
        this.searching = false
        this.searched = true
      }
    },

    pick(candidate) {
      this.picked = this.picked && this.picked.id === candidate.id ? null : candidate
    },

    async addValidator() {
      if (!this.canSubmit || this.adding) return

      const target = this.picked
        ? { user_id: this.picked.id }
        : { email: this.term.trim(), name: this.inviteName.trim() || null }

      this.adding = true

      try {
        await this.service.addValidator(this.referenceType, this.referenceId, target, this.applyToAll)
        // Recarrega em vez de empurrar na lista: em "todos os eventos" o servidor
        // grava várias linhas, e a desta tela pode não ser a devolvida.
        await this.loadValidators()
        this.resetForm()
      } catch (error) {
        // toast do Rest
      } finally {
        this.adding = false
      }
    },

    resetForm() {
      this.term = ''
      this.candidates = []
      this.picked = null
      this.inviteName = ''
      this.applyToAll = false
      this.searched = false
    },

    async removeValidator(validator) {
      try {
        await this.service.removeValidator(validator.id)
        this.validators = this.validators.filter(item => item.id !== validator.id)
      } catch (error) {
        // toast do Rest
      }
    },

    async undo(entry) {
      try {
        await this.service.undoCheckIn(entry.id)
        await this.reload()
      } catch (error) {
        // toast do Rest
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.cp-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.cp-header__actions {
  display: flex;
  align-items: center;
}

.cp-date {
  height: 2.4rem;
  padding: 0 0.6rem;
  border: 1px solid #d8d6de;
  border-radius: 0.35rem;
}

.cp-muted {
  color: #6e6b7b;
  font-size: 0.82rem;
}

.cp-metric {
  text-align: center;
}

.cp-metric__value {
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.cp-metric__label {
  margin-top: 0.2rem;
  color: #6e6b7b;
  font-size: 0.76rem;
}

.cp-table {
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 0.4rem 0.5rem;
    border-bottom: 1px solid #ebe9f1;
    color: #6e6b7b;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-align: left;
  }

  td {
    padding: 0.55rem 0.5rem;
    border-bottom: 1px solid #f3f2f7;
    font-size: 0.85rem;
  }
}

.cp-num {
  font-variant-numeric: tabular-nums;
}

.cp-code {
  font-family: 'Courier New', Courier, monospace;
}

.cp-row--undone {
  opacity: 0.5;
  text-decoration: line-through;
}

.cp-actions {
  text-align: right;
}

.cp-link {
  border: 0;
  background: transparent;
  color: rgba(var(--vs-primary), 1);
  font-size: 0.8rem;
  cursor: pointer;

  &--danger {
    color: #ea5455;
  }
}

.cp-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.cp-list__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f2f7;

  &:last-child {
    border-bottom: 0;
  }
}

.cp-list__who {
  min-width: 0;
}

.cp-list__name {
  font-weight: 600;
  font-size: 0.88rem;
}

.cp-list__meta {
  font-size: 0.75rem;
  overflow-wrap: anywhere;
}

/* ---------------------------------------------------------------- */
/* Designar validador                                                */
/* ---------------------------------------------------------------- */
.cp-label {
  display: block;
  margin-bottom: 0.3rem;
  color: #6e6b7b;
  font-size: 0.78rem;
}

.cp-input {
  width: 100%;
  height: 2.4rem;
  padding: 0 0.7rem;
  border: 1px solid #d8d6de;
  border-radius: 0.35rem;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: rgba(var(--vs-primary), 1);
  }
}

.cp-candidates {
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
}

.cp-candidate {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0.35rem;
  padding: 0.45rem 0.6rem;
  border: 1px solid #d8d6de;
  border-radius: 0.35rem;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &--on {
    border-color: rgba(var(--vs-primary), 1);
    background: rgba(var(--vs-primary), 0.08);
  }
}

.cp-invite {
  color: #6e6b7b;
  font-size: 0.82rem;
}

.cp-checkbox {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  color: #6e6b7b;
  font-size: 0.82rem;
  cursor: pointer;
}

.cp-badge {
  margin-left: 0.35rem;
  padding: 0.05rem 0.4rem;
  border-radius: 0.6rem;
  background: rgba(255, 159, 67, 0.16);
  color: #ff9f43;
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;
}
</style>
